import { $, $$, applyDataStyles, setLoading } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { formToObject } from '../lib/form.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';

/** The form's tracker, so a deliberate reload does not ask about unsaved edits. */
let tracker: ReturnType<typeof trackDirtyForm> | null = null;
import { toastError, toastSuccess } from '../lib/toast.js';

export function initCustomBotPage(): void {
  const form = $<HTMLFormElement>('[data-custom-bot-form]');
  const guildId =
    form?.dataset.guildId ?? $<HTMLElement>('[data-bot-start]')?.dataset.guildId ?? '';

  profileImages(guildId);

  const statusBadge = $('[data-bot-status]');
  const startButton = $<HTMLButtonElement>('[data-bot-start]');
  const stopButton = $<HTMLButtonElement>('[data-bot-stop]');

  const setStatus = (label: string, badgeClass: string) => {
    if (!statusBadge) return;
    statusBadge.className = `badge ${badgeClass}`;
    statusBadge.textContent = label;
  };

  startButton?.addEventListener('click', () => {
    void (async () => {
      setLoading(startButton, true);
      setStatus(ct('starting', 'Starting…'), 'badge-warning');
      try {
        const result = await api.post<{ tag: string }>(`/api/custom-bot/start?guildId=${guildId}`);

        setStatus(ct('botOnline', 'Online'), 'badge-success');
        if (stopButton) stopButton.disabled = false;
        toastSuccess(ct('botStarted', 'Bot started.').replace('{tag}', result.tag));
      } catch (error) {
        setStatus(ct('botFailed', 'Error'), 'badge-error');
        toastError(error);
      } finally {
        setLoading(startButton, false);
      }
    })();
  });

  stopButton?.addEventListener('click', () => {
    void (async () => {
      setLoading(stopButton, true);
      try {
        await api.post(`/api/custom-bot/stop?guildId=${guildId}`);
        setStatus(ct('stopped', 'Stopped'), 'badge-ghost');
        stopButton.disabled = true;
        toastSuccess(ct('botStopped', 'Bot stopped.'));
      } catch (error) {
        toastError(error);
      } finally {
        setLoading(stopButton, false);
      }
    })();
  });

  if (!form) return;

  tracker = trackDirtyForm({
    form,
    successMessage: ct('configSaved', 'Configuration saved.'),
    onSave: async () => {
      const values = formToObject(form);
      await api.put(`/api/custom-bot?guildId=${guildId}`, {
        ...values,
        guildId,
      });

      // The token is not shown again: we clear the field after saving so the
      // user can plainly see there is no need to type it in again.
      const tokenField = form.elements.namedItem('botToken');
      if (tokenField instanceof HTMLInputElement) {
        tokenField.value = '';
        tokenField.placeholder = ct('tokenKept', 'Token saved - leave empty to keep it');
      }
    },
  });
}

/**
 * The bot's own picture, changed on Discord.
 *
 * Sent as it was chosen — no canvas, no resizing: Discord accepts PNG, JPEG,
 * GIF and WebP and says so itself when it does not, and a picture silently
 * re-encoded in the browser is a picture that arrives different from the one
 * somebody picked.
 */
function profileImages(guildId: string): void {
  for (const input of $$<HTMLInputElement>('[data-bot-image]')) {
    input.addEventListener('change', () => {
      void (async () => {
        const file = input.files?.[0];
        if (!file) return;

        const field = input.dataset.botImage === 'banner' ? 'banner' : 'avatar';
        const body = new FormData();
        body.append('image', file);
        body.append('guildId', guildId);

        try {
          const answer = await api.post<{ avatarUrl: string; bannerUrl: string }>(
            `/api/custom-bot/image/${field}?guildId=${guildId}`,
            body,
          );

          paint(answer);
          toastSuccess(ct('botImageSaved', 'Picture updated on Discord.'));
        } catch (error) {
          toastError(error);
        } finally {
          // Cleared so choosing the same file twice fires the event again.
          input.value = '';
        }
      })();
    });
  }
}

/** Shows what Discord answered, rather than what was uploaded. */
function paint(profile: { avatarUrl: string; bannerUrl: string }): void {
  const banner = $<HTMLElement>('[data-bot-banner]');
  if (banner && profile.bannerUrl) {
    banner.dataset.backgroundImage = profile.bannerUrl;
    applyDataStyles(banner.parentElement ?? document);
  }

  const avatar = $<HTMLImageElement>('[data-bot-avatar]');
  if (avatar && profile.avatarUrl) avatar.src = profile.avatarUrl;

  /*
   * The placeholder is replaced rather than hidden: a first upload has no
   * image tag to point at, and leaving the robot beside the new picture would
   * be worse than reloading.
   */
  const empty = $<HTMLElement>('[data-bot-avatar-empty]');
  if (empty && profile.avatarUrl) {
    // Released first, or the navigation guard asks whether to leave — and a
    // "yes" discards edits the reload was never meant to touch.
    tracker?.markClean();
    location.reload();
  }
}
